import { ReactElement } from 'react';

import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { GetInvoicesOpenAmountSummaryRecord, GetInvoicesResponse, OpenAmountItemResponse } from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { Box, ButtonLink, Card, Heading, Stack, Text } from '@eneco/sparky';
import { Attention, CircleCheckmark } from '@eneco/sparky/illustrations';
import { styled } from '@eneco/sparky/stitches';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { MyEnecoInvoiceRendering } from '@sitecore/types/MyEnecoInvoice';

import { redirectToPayment } from '../../../utils/payment/redirectToPayment';

const AlignSelfCenter = styled('div', {
  '@md': { alignSelf: 'center' },
});

export type PaymentMethodType =
  | 'DirectDebit'
  | 'DirectDebitExcludingYearlyBill'
  | 'BankTransfer'
  | 'PaymentMethodCombo';

export interface HandlerContext {
  records: GetInvoicesOpenAmountSummaryRecord[];
  openAmountItems?: OpenAmountItemResponse[];
  unpaidDirectDebitYearlyBills?: GetInvoicesResponse['invoices'];
}

// Component definitions
const NoActionsRequiredMessage = ({ message }: { message: string }) => (
  <Box paddingBottom={4}>
    <Card>
      <Box padding="6">
        <Stack direction="row" alignX="start" alignY="center" gap={2}>
          <Stack gap="1">
            <CircleCheckmark color="feedbackSuccess" size="large" />
          </Stack>
          <AlignSelfCenter>
            <Text size="BodyM" weight="bold">
              {message}
            </Text>
          </AlignSelfCenter>
        </Stack>
      </Box>
    </Card>
  </Box>
);

const WithOpenAmount = ({
  amount,
  invoiceIds,
  isDueOpenAmount,
  isDirectDebit,
  hasMultipleBillingAccounts,
}: {
  amount: number;
  invoiceIds?: string[];
  isDueOpenAmount?: boolean;
  isDirectDebit: boolean;
  hasMultipleBillingAccounts: boolean;
}) => {
  const { format, currency } = useFormatter();
  const { trackEventBE } = useTracking();
  const { fields } = useContent<MyEnecoInvoiceRendering>();
  const { selectedAccount, isCurrentAccountReader } = useSelfServiceAccount();

  const onPayNowClick = () => {
    trackEventBE('be_billing_payment_total');
    redirectToPayment(selectedAccount.crmAccountNumber, invoiceIds?.join(',') ?? '');
  };

  const isPaymentDisabled = fields.invoiceDetails.disableOnlinePaymentsCheckbox?.value ?? false;

  const { filters } = fields;

  const directDebitBody = isDueOpenAmount
    ? (filters.dueOpenAmountDirectDebitText?.value ??
      'Je hebt nog een opstaand bedrag van €{amount}. We bieden het openstaande bedrag aan bij de bank.')
    : (filters.openAmountDirectDebitText?.value ?? 'Alles is in orde! We bieden je volgende betaling aan bij de bank.');

  const bankTransferBody = isDueOpenAmount
    ? (filters.dueOpenAmountBankTransferText?.value ??
      'Betaalherinnering, je hebt nog een openstaand bedrag van €{amount}!')
    : (filters.openAmountBankTransferText?.value ?? 'Opgelet, je hebt nog een openstaand bedrag van €{amount}!');

  return (
    <Box paddingBottom={4}>
      <Card as="div" corners="rounded" elevation="S">
        <Box padding="6">
          <Stack direction={{ initial: 'column', md: 'row' }} alignX="start" gap={2} alignY="center">
            <Stack gap="1">
              <Attention color="feedbackError" size="large" />
            </Stack>
            <Stack gap="1">
              <Text size="BodyM" weight="bold">
                {format(isDirectDebit ? directDebitBody : bankTransferBody, { amount: currency.euro(amount) })}
              </Text>
            </Stack>
            {!isCurrentAccountReader &&
              (!hasMultipleBillingAccounts || invoiceIds?.length === 1) &&
              !isDirectDebit &&
              !isPaymentDisabled && (
                <AlignSelfCenter>
                  <ButtonLink size="compact" onClick={onPayNowClick}>
                    {fields.filters.payNowButtonLink.value.text ?? 'Betaal nu [not in sitecore]'}
                  </ButtonLink>
                </AlignSelfCenter>
              )}
          </Stack>
        </Box>
      </Card>
    </Box>
  );
};

const RefundMessage = ({ amount, iban }: { amount: number; iban: string }) => {
  const { currency, format } = useFormatter();
  const { fields } = useContent<MyEnecoInvoiceRendering>();

  return (
    <Card as="div" corners="rounded" elevation="S">
      <Box padding="6">
        <Stack direction={{ initial: 'column', md: 'row' }} alignX={{ md: 'justify' }} gap={2}>
          <Stack gap="1">
            <Heading as="h3" size="S" color={'textHighlightVarFive'}>
              {currency.euro(amount)}
            </Heading>
            <Text size="BodyM" weight="bold" color={'textHighlightVarFive'}>
              {format(
                fields.filters.reimbursementText.value ??
                  'Eneco zal dit bedrag terugstorten op jouw rekeningnummer {iban} [not in sitecore]',
                { iban },
              )}
            </Text>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
};

// Component helper functions
const InvoiceHeading = () => {
  const { fields } = useContent<MyEnecoInvoiceRendering>();
  return (
    <Heading size="S" as="h2">
      {fields.filters.openAmountTitle.value ?? 'Openstaand bedrag [not in sitecore]'}
    </Heading>
  );
};

const NoActionsRequiredMessageWithHeading = ({ message }: { message: string }) => (
  <>
    <InvoiceHeading />
    <NoActionsRequiredMessage message={message} />
  </>
);

const SingleBillingAccountWithOpenAmountWithHeading = ({
  amount,
  count,
  invoiceIds,
  hasMultipleBillingAccounts,
}: {
  amount: number;
  count: number;
  invoiceIds: string[];
  hasMultipleBillingAccounts: boolean;
}) => (
  <>
    <InvoiceHeading />
    <WithOpenAmount
      amount={amount}
      invoiceIds={invoiceIds}
      isDueOpenAmount={false}
      hasMultipleBillingAccounts={hasMultipleBillingAccounts}
      isDirectDebit={false}
    />
  </>
);

const DueOpenAmountWithHeading = ({
  amount,
  count,
  invoiceIds,
  hasMultipleBillingAccounts,
}: {
  amount: number;
  count: number;
  invoiceIds: string[];
  hasMultipleBillingAccounts: boolean;
}) => (
  <>
    <InvoiceHeading />
    <WithOpenAmount
      amount={amount}
      invoiceIds={invoiceIds}
      isDueOpenAmount={true}
      isDirectDebit={false}
      hasMultipleBillingAccounts={hasMultipleBillingAccounts}
    />
  </>
);

// Standalone message components that use useContent
const DirectDebitIncludingYearlyBillMessage = () => {
  const { fields } = useContent<MyEnecoInvoiceRendering>();
  return (
    <NoActionsRequiredMessageWithHeading
      message={
        fields.filters.directDebitIncludingYearlyBillText.value ??
        'Alles is in orde! Je volgende factuur zal aangeboden worden bij je bank. [not in sitecore]'
      }
    />
  );
};

const DirectDebitIncludingYearlyBillWithDueOpenAmountMessage = ({
  amount,
  hasMultipleBillingAccounts,
}: {
  amount: number;
  hasMultipleBillingAccounts: boolean;
}) => {
  return (
    <>
      <InvoiceHeading />
      <WithOpenAmount
        amount={amount}
        isDueOpenAmount={true}
        isDirectDebit={true}
        hasMultipleBillingAccounts={hasMultipleBillingAccounts}
      />
    </>
  );
};

const BankTransferNoOpenAmountMessage = () => {
  const { fields } = useContent<MyEnecoInvoiceRendering>();
  return (
    <NoActionsRequiredMessageWithHeading
      message={
        fields.filters.noOpenAmountText.value ??
        'Alles is in orde! Je hebt geen openstaand bedrag voor al je producten. [not in sitecore]'
      }
    />
  );
};

export interface SingleAccountHandlers {
  hasOpenAmountOrPaid?: () => ReactElement;
  hasDueOpenAmountDirectDebit?: (record: GetInvoicesOpenAmountSummaryRecord) => ReactElement;
  hasUnpaidYearlyBills?: () => ReactElement;
  hasDueUnpaidYearlyBills?: () => ReactElement;
  hasOpenAmount?: (record: GetInvoicesOpenAmountSummaryRecord) => ReactElement;
  hasDueOpenAmount?: (record: GetInvoicesOpenAmountSummaryRecord) => ReactElement;
  hasPaidEverything?: () => ReactElement;
  hasRefund?: (amount: number, iban: string) => ReactElement;
}

export interface MultipleAccountHandlers {
  hasOpenAmount?: () => ReactElement;
  hasPaidEverything?: () => ReactElement;
  hasRefund?: () => ReactElement;
}

export const createInvoiceHandlers = (
  context: HandlerContext,
  hasMultipleBillingAccounts: boolean,
): Map<PaymentMethodType, SingleAccountHandlers> => {
  const { openAmountItems: invoices, unpaidDirectDebitYearlyBills } = context;

  const normalInvoices = invoices?.filter(invoice => invoice.invoiceType !== 'YearlyBill') ?? [];

  const openAmountYearlyBill = unpaidDirectDebitYearlyBills?.reduce(
    (sum, invoice) => sum + (invoice.openAmount ?? 0),
    0,
  );

  const openAmountNormalInvoices = normalInvoices?.reduce((sum, invoice) => sum + (invoice.amount ?? 0), 0);

  return new Map([
    [
      'DirectDebit',
      {
        hasDueOpenAmountDirectDebit: (record: GetInvoicesOpenAmountSummaryRecord) => (
          <DirectDebitIncludingYearlyBillWithDueOpenAmountMessage
            amount={record.amount ?? 0}
            hasMultipleBillingAccounts={hasMultipleBillingAccounts}
          />
        ),
        hasOpenAmountOrPaid: () => <DirectDebitIncludingYearlyBillMessage />,
        hasRefund: (amount: number, iban: string) => (
          <>
            <InvoiceHeading />
            <RefundMessage amount={amount ?? 0} iban={iban ?? ''} />
          </>
        ),
      },
    ],
    [
      'DirectDebitExcludingYearlyBill',
      {
        hasDueUnpaidYearlyBills: () => {
          return (
            <DueOpenAmountWithHeading
              amount={openAmountYearlyBill ?? 0}
              count={unpaidDirectDebitYearlyBills?.length ?? 0}
              invoiceIds={unpaidDirectDebitYearlyBills?.map(invoice => invoice.invoiceId) ?? []}
              hasMultipleBillingAccounts={hasMultipleBillingAccounts}
            />
          );
        },
        hasUnpaidYearlyBills: () => {
          return (
            <SingleBillingAccountWithOpenAmountWithHeading
              amount={openAmountYearlyBill ?? 0}
              count={unpaidDirectDebitYearlyBills?.length ?? 0}
              invoiceIds={unpaidDirectDebitYearlyBills?.map(invoice => invoice.invoiceId) ?? []}
              hasMultipleBillingAccounts={hasMultipleBillingAccounts}
            />
          );
        },
        hasDueOpenAmount: (record: GetInvoicesOpenAmountSummaryRecord) => (
          <DirectDebitIncludingYearlyBillWithDueOpenAmountMessage
            amount={openAmountNormalInvoices ?? 0}
            hasMultipleBillingAccounts={hasMultipleBillingAccounts}
          />
        ),
        hasOpenAmountOrPaid: () => <DirectDebitIncludingYearlyBillMessage />,
        hasRefund: (amount: number, iban: string) => (
          <>
            <InvoiceHeading />
            <RefundMessage amount={amount ?? 0} iban={iban ?? ''} />
          </>
        ),
      },
    ],
    [
      'BankTransfer',
      {
        hasDueOpenAmount: (record: GetInvoicesOpenAmountSummaryRecord) => {
          const count = record.openAmountItems.length;
          return (
            <DueOpenAmountWithHeading
              amount={record.amount ?? 0}
              count={count ?? 0}
              invoiceIds={invoices?.map(invoice => invoice.invoiceNumber) ?? []}
              hasMultipleBillingAccounts={hasMultipleBillingAccounts}
            />
          );
        },
        hasOpenAmount: (record: GetInvoicesOpenAmountSummaryRecord) => {
          const count = record.openAmountItems.length;
          return (
            <SingleBillingAccountWithOpenAmountWithHeading
              amount={record.amount ?? 0}
              count={count ?? 0}
              invoiceIds={invoices?.map(invoice => invoice.invoiceNumber) ?? []}
              hasMultipleBillingAccounts={hasMultipleBillingAccounts}
            />
          );
        },
        hasPaidEverything: () => <BankTransferNoOpenAmountMessage />,
        hasRefund: (amount: number, iban: string) => (
          <>
            <InvoiceHeading />
            <RefundMessage amount={amount ?? 0} iban={iban ?? ''} />
          </>
        ),
      },
    ],
  ]);
};

export const getPaymentMethodType = (paymentMethods: Set<string>): PaymentMethodType | null => {
  const hasOnlyDirectDebit = paymentMethods.size === 1 && paymentMethods.has('DirectDebit');
  const hasOnlyDirectDebitExcludingYearlyBill =
    paymentMethods.size === 1 && paymentMethods.has('DirectDebitExcludingYearlyBill');
  const hasOnlyBankTransfer = paymentMethods.size === 1 && paymentMethods.has('BankTransfer');
  const hasPaymentMethodCombo = paymentMethods.size > 1;

  if (hasOnlyDirectDebit) return 'DirectDebit';
  if (hasOnlyDirectDebitExcludingYearlyBill) return 'DirectDebitExcludingYearlyBill';
  if (hasOnlyBankTransfer) return 'BankTransfer';
  if (hasPaymentMethodCombo) return 'PaymentMethodCombo';
  return null;
};
